import React, { useContext, useEffect, useState } from 'react';
import iframeContext, { IIframeContext } from '../../contexts/IframeContext';
import { FrameData } from '../../components/FrameData';
import { RouteComponentProps } from 'react-router-dom';
import {
  createOcorrencia,
  createOcorrenciaAnonima,
  uploadMultipleFilesOcorrencia,
} from '../../api/services/OcorrenciaPublica';
import { prefix } from '../../router';
import { useToast } from '../../hooks/toast';
import useLocalStorage, { getItemInStorage } from '../../hooks/useLocalStorage';
import { IReclamante } from '../../dtos/Reclamante';
import { AutoRow } from 'components/AutoRow';
import { Title } from 'components/AutoRow/components/Title';
import { Paragraph } from 'components/AutoRow/components/Paragraph';
import Input from 'components/AutoRow/components/Input';
import { Button } from 'components/AutoRow/components/Button';
import SelectTipoOcorrencia from 'helpers/SelectTipoOcorrencia';
import { FileInput, isChangeEvent } from 'components/AutoRow/components/FileInput';
import { serializeLocalFile } from 'hooks/helpers/methods';

interface IFormData {
  dsc_observacao?: string;
  seq_tipo_ocorrencia?: number;
}
export interface IFileInfo {
  id: string;
  url?: string;
  file?: File;
  name: string;
  type: string;
  date: string;
}
const IframeSend = ({ history }: RouteComponentProps) => {
  const [dadosReclamante] = useLocalStorage<IReclamante>('reclamante', {});
  const { setIframeState, iframeState } = useContext<IIframeContext>(iframeContext);
  const [formData, setFormData] = useState<IFormData>({});
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [loadingPage, setLoadingPage] = useState(false);
  const [files, setFiles] = React.useState<IFileInfo[]>([]);
  const toast = useToast();
  const [codParqueServico] = useLocalStorage<number>('cod_parque_servico', -1);
  let inProgress = false;

  function handleRedirectToAddress() {
    const isOcorrenciaComPonto = !!iframeState?.dadosCadastroOcorrencia?.ocorrenciasPontoServico?.[0]
      ?.cod_ponto_servico;

    history.push(isOcorrenciaComPonto ? `${prefix}/home` : `${prefix}/generate/address`);
  }
  const handleOnChangeFilters = (event) => {
    const name = event?.name || event?.target?.name;
    const value = event?.value || event?.target?.value;
    setFormData((old) => ({ ...old, [name]: value }));
  };
  async function handleCreateOcorrencia(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!formData.seq_tipo_ocorrencia) {
      toast.addToast({
        type: 'warn',
        description: 'Nenhum tipo de ocorrencia foi selecionado!',
      });
      return;
    }

    if (inProgress) return;
    inProgress = true;

    setLoadingSubmit(true);
    let response;
    const hasAccessToken = !!getItemInStorage('accessToken');
    const ocorrenciasPontoServico =
      iframeState?.dadosCadastroOcorrencia?.ocorrenciasPontoServico?.map((ops) => ({
        ...ops,
        cod_tipo_ocorrencia: formData.seq_tipo_ocorrencia,
        dsc_observacao: formData.dsc_observacao,
        json_ocorrencia_ponto_servico: {
          ...ops.json_ocorrencia_ponto_servico,
          imagens: ops.json_ocorrencia_ponto_servico?.imagens || [{ nom_caminho_imagem: null }],
        },
      })) || [];
    const arquivosUpload = files.map((file) => file.file).filter(Boolean) as File[];
    let arquivos = files.map((file) => ({
      nome: file.name,
      nome_original: file.name,
      tipo: file.type,
      arquivo: file.url,
      base64: file.url,
      latitude: ocorrenciasPontoServico[0]?.json_ocorrencia_ponto_servico?.coords?.latitude,
      longitude: ocorrenciasPontoServico[0]?.json_ocorrencia_ponto_servico?.coords?.longitude,
    }));

    if (hasAccessToken && arquivosUpload.length) {
      const uploadResponse = await uploadMultipleFilesOcorrencia(arquivosUpload);

      if (uploadResponse.err || uploadResponse?.resp?.err) {
        inProgress = false;
        setLoadingSubmit(false);
        toast.addToast({ type: 'warn', description: uploadResponse.err || uploadResponse?.resp?.err });
        return;
      }

      arquivos = uploadResponse.resp || [];
    }

    const dados = {
      ocorrencia: {
        ...iframeState?.dadosCadastroOcorrencia?.ocorrencia,
        cod_reclamante: dadosReclamante.seq_reclamante,
      },
      ocorrenciasPontoServico,
      arquivos,
    };

    if (!hasAccessToken) {
      response = await createOcorrenciaAnonima(dados);
    } else {
      response = await createOcorrencia(dados);
    }
    inProgress = false;
    setLoadingSubmit(false);

    if (response?.err || response?.resp?.err) {
      toast.addToast({ type: 'warn', description: response?.err || response?.resp?.err });
      return;
    }

    toast.addToast({
      type: 'success',
      description: 'Ocorrência cadastrada com sucesso!',
    });

    if (setIframeState) {
      setIframeState((oldValue) => ({
        ...oldValue,
        dadosCadastroOcorrencia: {},
      }));
    }

    history.push(`${prefix}/home`);
  }
  const handleSelectFiles = React.useCallback(
    async (e: React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLInputElement>) => {
      const targetFiles = isChangeEvent(e) ? e.target.files : e.dataTransfer.files;
      const serializedFiles = await serializeLocalFile(targetFiles);
      setFiles(serializedFiles);
    },
    [],
  );
  useEffect(() => {
    if (!iframeState || (iframeState && !iframeState?.dadosCadastroOcorrencia)) {
      history.push(`${prefix}/home`);
    }
  }, [iframeState]);

  return (
    <FrameData loading={loadingPage}>
      <form onSubmit={handleCreateOcorrencia}>
        <AutoRow>
          <Title label="Geração de Ocorrência" />
          <Paragraph marginBottom={26} marginTop={-4} label="Para finalizar, informe os dados finais necessários" />

          <SelectTipoOcorrencia
            isObrigatorio
            cod_parque_servico={codParqueServico}
            handleFunction={handleOnChangeFilters}
            value={formData.seq_tipo_ocorrencia as any}
            anonimo={!getItemInStorage('accessToken')}
            is_iframe={true}
            name="seq_tipo_ocorrencia"
          />
          <Input
            handleFunction={handleOnChangeFilters}
            label="Observação"
            name="dsc_observacao"
            value={formData.dsc_observacao}
          />
          <FileInput multiple label={`Adicionar Arquivo`} accept="image/*" onChange={handleSelectFiles} />
          <Button onClick={handleRedirectToAddress} backgroundColor="light">
            Cancelar
          </Button>
          <Button loading={loadingSubmit} type="submit">
            Enviar
          </Button>
        </AutoRow>
      </form>
    </FrameData>
  );
};

export default IframeSend;
